// packages/api-client/src/services/suggestionsService.ts
//
// Suggestions API service (axios-based, consistent with other services).
// Endpoints:
// - GET /api/v1/agents/{agent_id}/suggestions?limit&scope&force
// - GET /api/v1/conversations/{conversation_id}/suggestions?limit&scope&force
//
// Response:
// { "suggestions": [ { "title": "Short label", "prompt": "Full suggested next user message" } ] }

import { getApiClient } from '../client';
import { handleApiError } from '../utils/handleApiError';
import type { ServiceResponse } from '@dopomogai/types';

export type SuggestionItem = {
  id?: string;
  title?: string;
  prompt: string;
  category?: string;
};

export type SuggestionsResponse = {
  suggestions: SuggestionItem[];
};

export const suggestionsService = {
  /**
   * Agent-scoped suggestions (new conversation hero).
   * Default scope=new_conversation; limit default 4.
   */
  getAgentSuggestions: (
    agentId: string,
    params?: { limit?: number; scope?: string; force?: boolean },
  ): ServiceResponse<SuggestionsResponse> => {
    const limit = params?.limit ?? 4;
    const scope = params?.scope ?? 'new_conversation';
    const force = params?.force ?? false;

    return getApiClient()
      .get<SuggestionsResponse>(`/agents/${agentId}/suggestions`, { params: { limit, scope, force } })
      .then((res) => res.data)
      .catch((err) => handleApiError(err, `fetching agent suggestions for ${agentId}`));
  },

  /**
   * Conversation-scoped suggestions (next message).
   * Default scope=next_message; limit default 4.
   */
  getConversationSuggestions: (
    conversationId: string,
    params?: { limit?: number; scope?: string; force?: boolean },
  ): ServiceResponse<SuggestionsResponse> => {
    const limit = params?.limit ?? 4;
    const scope = params?.scope ?? 'next_message';
    const force = params?.force ?? false;

    return getApiClient()
      .get<SuggestionsResponse>(`/conversations/${conversationId}/suggestions`, {
        params: { limit, scope, force },
      })
      .then((res) => res.data)
      .catch((err) => handleApiError(err, `fetching conversation suggestions for ${conversationId}`));
  },
};